/**
 * Client-side mirror of functions/lib/aiSpendBudget.js resolution
 * (ticket override → project → workspace → global fallback).
 */

export const GLOBAL_AI_SPEND_CAP = Object.freeze({
  maxCallCount: 25,
  maxSpendUsd: 3.0,
});

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function readPositiveNumber(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * @param {object|null|undefined} ticket
 * @returns {{ callCount: number, spendUsd: number }}
 */
export function readTicketAiSpend(ticket) {
  const spend =
    ticket && ticket.aiSpend && typeof ticket.aiSpend === 'object'
      ? ticket.aiSpend
      : {};
  return {
    callCount: Math.max(0, Math.floor(Number(spend.callCount) || 0)),
    spendUsd: Math.max(0, Number(spend.spendUsd) || 0),
  };
}

/**
 * @param {object|null|undefined} raw
 * @param {{ maxCallCount: number, maxSpendUsd: number }} fallback
 */
function normalizeCap(raw, fallback) {
  const base = fallback || GLOBAL_AI_SPEND_CAP;
  if (!raw || typeof raw !== 'object') {
    return {
      maxCallCount: base.maxCallCount,
      maxSpendUsd: base.maxSpendUsd,
    };
  }
  return {
    maxCallCount:
      readPositiveNumber(raw.maxCallCount) != null
        ? readPositiveNumber(raw.maxCallCount)
        : base.maxCallCount,
    maxSpendUsd:
      readPositiveNumber(raw.maxSpendUsd) != null
        ? readPositiveNumber(raw.maxSpendUsd)
        : base.maxSpendUsd,
  };
}

/**
 * @param {object|null|undefined} raw
 * @returns {boolean}
 */
function hasConfiguredCap(raw) {
  return Boolean(
    raw &&
      typeof raw === 'object' &&
      (readPositiveNumber(raw.maxCallCount) != null ||
        readPositiveNumber(raw.maxSpendUsd) != null),
  );
}

/**
 * @param {object|null|undefined} companyOrSettings
 * @returns {object|null}
 */
export function readWorkspaceAiSpendCap(companyOrSettings) {
  if (!companyOrSettings || typeof companyOrSettings !== 'object') {
    return null;
  }
  if (hasConfiguredCap(companyOrSettings.aiSpendCap)) {
    return companyOrSettings.aiSpendCap;
  }
  const settings =
    companyOrSettings.settings &&
    typeof companyOrSettings.settings === 'object'
      ? companyOrSettings.settings
      : null;
  if (settings && hasConfiguredCap(settings.aiSpendCap)) {
    return settings.aiSpendCap;
  }
  if (hasConfiguredCap(companyOrSettings)) {
    return companyOrSettings;
  }
  return null;
}

/**
 * @param {object|null|undefined} ticket
 * @param {object|null|undefined} projectDoc
 * @param {object|null|undefined} [companyOrSettings]
 * @returns {{
 *   maxCallCount: number,
 *   maxSpendUsd: number,
 *   source: string,
 * }}
 */
export function resolveEffectiveAiSpendCap(
  ticket,
  projectDoc,
  companyOrSettings,
) {
  const workspaceRaw = readWorkspaceAiSpendCap(companyOrSettings);
  const hasWorkspaceCap = hasConfiguredCap(workspaceRaw);
  const workspaceCap = normalizeCap(workspaceRaw, GLOBAL_AI_SPEND_CAP);

  const hasProjectCap = hasConfiguredCap(
    projectDoc && projectDoc.aiSpendCap,
  );
  const projectCap = normalizeCap(
    projectDoc && projectDoc.aiSpendCap,
    hasWorkspaceCap ? workspaceCap : GLOBAL_AI_SPEND_CAP,
  );

  const mergeBase = hasProjectCap
    ? projectCap
    : hasWorkspaceCap
      ? workspaceCap
      : GLOBAL_AI_SPEND_CAP;

  const override =
    ticket &&
    ticket.aiSpendCapOverride &&
    typeof ticket.aiSpendCapOverride === 'object'
      ? ticket.aiSpendCapOverride
      : null;
  const hasOverride =
    override &&
    (readPositiveNumber(override.maxCallCount) != null ||
      readPositiveNumber(override.maxSpendUsd) != null);

  if (hasOverride) {
    const merged = normalizeCap(override, mergeBase);
    return { ...merged, source: 'ticket_override' };
  }
  if (hasProjectCap) {
    return { ...projectCap, source: 'project_default' };
  }
  if (hasWorkspaceCap) {
    return { ...workspaceCap, source: 'workspace_default' };
  }
  return { ...GLOBAL_AI_SPEND_CAP, source: 'global_fallback' };
}

/**
 * Keep in sync with functions/lib/aiSpendBudget.js
 * @param {object|null|undefined} ticket
 * @returns {boolean}
 */
export function isAiBudgetExceeded(ticket) {
  return Boolean(ticket && ticket.aiBudgetExceeded === true);
}

/**
 * @param {object|null|undefined} ticket
 * @param {object|null|undefined} projectDoc
 * @param {object|null|undefined} [companyOrSettings]
 * @returns {boolean}
 */
export function isAiSpendOverCap(ticket, projectDoc, companyOrSettings) {
  const { callCount, spendUsd } = readTicketAiSpend(ticket);
  const cap = resolveEffectiveAiSpendCap(
    ticket,
    projectDoc,
    companyOrSettings,
  );
  return callCount >= cap.maxCallCount || spendUsd >= cap.maxSpendUsd;
}

/**
 * @param {object|null|undefined} ticket
 * @param {object|null|undefined} projectDoc
 * @param {object|null|undefined} [companyOrSettings]
 * @returns {boolean}
 */
export function isTicketAiSpendBlocked(
  ticket,
  projectDoc,
  companyOrSettings,
) {
  return (
    isAiBudgetExceeded(ticket) ||
    isAiSpendOverCap(ticket, projectDoc, companyOrSettings)
  );
}

export const AI_SPEND_BLOCKED_MESSAGE =
  'No AI spend budget remaining for this ticket. Reset the budget or raise the cap to continue.';

/**
 * @param {number} n
 * @returns {string}
 */
export function formatAiSpendUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
}
